import React from 'react';
import { Box, Stack, styled, Typography } from '@mui/material';
import { TextField } from 'components';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useFormContext } from 'react-hook-form';
import { SignupFormData, SignUpUserType } from 'features/signUp/types';
import { useSignupData } from 'features/signUp/hooks/useSignupData';

const InsuranceCard = styled(Box)(({ theme }) => ({
  width: '80%',
  height: 'auto',
  alignSelf: 'center',
})) as typeof Box;

interface MemberIdEligibilityCheckStepProps {
  signUpUserType: SignUpUserType;
}

export const MemberIdEligibilityCheckStep = ({
  signUpUserType,
}: MemberIdEligibilityCheckStepProps) => {
  const { translation } = useTranslationContext();
  const {
    register,
    formState: { errors },
  } = useFormContext<SignupFormData>();
  const { ref, onChange, onBlur, name, required } = register('memberId', {
    setValueAs: (value) => value.trim(),
    required: translation('empty_field_error'),
  });
  const { eligibilityFieldPhoto, eligibilityMessage } = useSignupData();

  return (
    <Stack className="member-id-eligibility-verification-step" spacing={4}>
      <Typography variant="h5" color="primary" id="eligibility-header">
        {signUpUserType === 'member'
          ? translation('eligibility_id_header')
          : translation('eligibility_id_caregiver_header')}
      </Typography>
      <Typography variant="body1" color="grey.800" id="eligibility-body">
        {signUpUserType === 'member'
          ? translation('eligibility_id_body')
          : translation('eligibility_id_caregiver_body')}
      </Typography>
      {!!eligibilityFieldPhoto && (
        <InsuranceCard
          component="img"
          src={eligibilityFieldPhoto}
          alt="Health Plan member ID card"
          className="member-id-eligibility-verification-step__insurance-card"
        />
      )}
      <Typography variant="caption" color="grey.800" textAlign="center" id="eligibility-message">
        {eligibilityMessage}
      </Typography>
      <TextField
        id="signup_member-id-input"
        label={translation('member_id')}
        name={name}
        required={required}
        onChange={onChange}
        onBlur={onBlur}
        inputRef={ref}
        error={!!errors.memberId}
        helperText={errors.memberId?.message}
        aria-describedby="eligibility-body eligibility-message"
      />
    </Stack>
  );
};
